// Relative import (works on Cloudflare Pages root=web/lobby; sibling web/shared).
import { ApiError, requestJSON } from '../../../shared/api.ts'

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

export type AuthUser = {
  id: number
  telegram_id: number
  username: string
  first_name: string
  last_name: string
  display_name?: string
  language_code: string
  status?: string
  balance: number
}

export type MeResponse = {
  user: AuthUser
  identities: { provider: string; subject: string; linked_at: string }[]
}

export type AuthResponse = {
  token: string
  user: AuthUser
}

export type Transaction = {
  id: number
  type: string
  amount: number
  game_type: string
  game_round_id: string
  balance_after: number
  created_at: string
}

export { ApiError }

async function request<T>(
  path: string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  return requestJSON<T>(path, { ...options, apiBase: API_BASE })
}

export async function loginWithInitData(initData: string): Promise<AuthResponse> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemoLobby } = await import('../../../shared/demo/adapters/lobby.ts')
    return loginDemoLobby()
  }
  return request<AuthResponse>('/api/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ init_data: initData }),
  })
}

/** Local SQLite play without Telegram Bot (Gateway debug mode only). */
export async function loginDev(body: {
  telegram_id?: number
  username?: string
  first_name?: string
} = {}): Promise<AuthResponse> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemoLobby } = await import('../../../shared/demo/adapters/lobby.ts')
    return loginDemoLobby()
  }
  return request<AuthResponse>('/api/auth/dev', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function getBalance(token: string): Promise<{ balance: number }> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { fetchBalance } = await import('../../../shared/demo/adapters/auth.ts')
    return fetchBalance()
  }
  return request<{ balance: number }>('/api/wallet/balance', { token })
}

export async function getMe(token: string): Promise<MeResponse> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoMe } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoMe()
  }
  return request<MeResponse>('/api/me', { token })
}

export async function getMyTransactions(
  token: string,
  limit = 20,
): Promise<{ transactions: Transaction[] }> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoTransactions } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoTransactions(limit)
  }
  return request<{ transactions: Transaction[] }>(
    `/api/me/transactions?limit=${limit}`,
    { token },
  )
}

export type LeaderboardEntry = {
  rank: number
  username: string
  score: number
}

export type RewardsStatus = {
  checkin_available: boolean
  relief_available: boolean
  next_available_at?: string
  relief_cooldown_ends_at?: string
  checkin_amount: number
  relief_amount: number
}

export type RewardGrant = {
  granted: boolean
  amount: number
  balance: number
  next_available_at?: string
}

export async function getRewardsStatus(token: string): Promise<RewardsStatus> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getRewardsStatus: status } = await import('../../../shared/demo/adapters/rewards.ts')
    return status()
  }
  return request<RewardsStatus>('/api/rewards/status', { token })
}

export async function postCheckin(token: string): Promise<RewardGrant> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { postCheckin: checkin } = await import('../../../shared/demo/adapters/rewards.ts')
    return checkin()
  }
  return request<RewardGrant>('/api/rewards/checkin', { method: 'POST', token })
}

export async function postRelief(token: string): Promise<RewardGrant> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { postRelief: relief } = await import('../../../shared/demo/adapters/rewards.ts')
    return relief()
  }
  return request<RewardGrant>('/api/rewards/relief', { method: 'POST', token })
}

export type ReferralInfo = {
  code: string
  invitees_count: number
  rewards_total: number
  start_payload: string
  inviter_bonus: number
  invitee_bonus: number
}

export async function getReferral(token: string): Promise<ReferralInfo> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoReferral } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoReferral()
  }
  return request<ReferralInfo>('/api/me/referral', { token })
}

export type NotifyPrefs = {
  checkin: boolean
  leaderboard: boolean
  bigwin: boolean
}

export async function getNotifyPrefs(token: string): Promise<NotifyPrefs> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoNotify } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoNotify()
  }
  return request<NotifyPrefs>('/api/me/notify', { token })
}

export async function putNotifyPrefs(
  token: string,
  body: Partial<NotifyPrefs>,
): Promise<NotifyPrefs> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoNotify } = await import('../../../shared/demo/adapters/lobby.ts')
    return { ...getDemoNotify(), ...body }
  }
  return request<NotifyPrefs>('/api/me/notify', {
    method: 'PUT',
    token,
    body: JSON.stringify(body),
  })
}

export type GameStats = {
  net_profit: number
  total_wagered: number
  total_won: number
  games_played: number
  biggest_win: number
}

export type MeStats = GameStats & {
  by_game: Record<string, GameStats>
}

export type GameHistoryItem = {
  round_id: string
  game_type: string
  wagered: number
  won: number
  refunded: number
  net: number
  created_at: string
}

export type GameHistory = {
  items: GameHistoryItem[]
  total: number
}

export async function getMyStats(token: string): Promise<MeStats> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoStats } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoStats()
  }
  return request<MeStats>('/api/me/stats', { token })
}

export async function getMyGameHistory(
  token: string,
  game: string,
  limit = 10,
  offset = 0,
): Promise<GameHistory> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoGameHistory } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoGameHistory(game, limit, offset)
  }
  return request<GameHistory>(
    `/api/me/games/${encodeURIComponent(game)}/history?limit=${limit}&offset=${offset}`,
    { token },
  )
}

export async function getLeaderboard(
  period: string,
  token?: string,
): Promise<LeaderboardEntry[]> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { getDemoLeaderboard } = await import('../../../shared/demo/adapters/lobby.ts')
    return getDemoLeaderboard()
  }
  const body = await request<{ entries?: LeaderboardEntry[] }>(
    `/api/leaderboard/${period}?game=all`,
    { token },
  )
  return body.entries ?? []
}

export async function resetDemoWallet(): Promise<{ balance: number }> {
  if (import.meta.env.VITE_DEMO_MODE !== 'true') {
    throw new Error('reset is demo-only')
  }
  const { resetDemoWallet: reset } = await import('../../../shared/demo/adapters/lobby.ts')
  return reset()
}

